import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'
import { getQuadrantInfo } from '@/lib/ideology2d'
import { QUADRANT_ICONS } from '@/constants/politicalMap'
import type { QuadrantName } from '@/constants/politicalMap'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// ============================================================================
// Types
// ============================================================================

interface PoliticalLabelProps {
  ideology: IdeologyVector
  showSubLabel?: boolean
  showIcon?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'badge' | 'inline' | 'card'
  className?: string
}

// ============================================================================
// Size Configurations
// ============================================================================

const sizeClasses = {
  sm: {
    container: 'text-xs',
    icon: 'text-sm',
    quadrant: 'text-xs font-medium',
    subLabel: 'text-[10px]',
    padding: 'px-2 py-0.5',
    cardPadding: 'px-2 py-1.5',
  },
  md: {
    container: 'text-sm',
    icon: 'text-base',
    quadrant: 'text-sm font-medium',
    subLabel: 'text-xs',
    padding: 'px-2.5 py-1',
    cardPadding: 'px-3 py-2',
  },
  lg: {
    container: 'text-base',
    icon: 'text-lg',
    quadrant: 'text-base font-semibold',
    subLabel: 'text-sm',
    padding: 'px-3 py-1.5',
    cardPadding: 'px-4 py-3',
  },
}

// ============================================================================
// Component
// ============================================================================

export function PoliticalLabel({
  ideology,
  showSubLabel = false,
  showIcon = true,
  size = 'md',
  variant = 'badge',
  className,
}: PoliticalLabelProps) {
  const quadrantInfo = getQuadrantInfo(ideology)
  const icon = QUADRANT_ICONS[quadrantInfo.quadrantName as QuadrantName] || ''
  const sizes = sizeClasses[size]

  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-1', sizes.container, className)}>
        {showIcon && <span className={sizes.icon}>{icon}</span>}
        <span className={sizes.quadrant}>{quadrantInfo.quadrantName}</span>
        {showSubLabel && (
          <span className={cn('text-muted-foreground', sizes.subLabel)}>
            ({quadrantInfo.subLabel})
          </span>
        )}
      </span>
    )
  }

  if (variant === 'card') {
    return (
      <div className={cn(
        'rounded-lg border bg-muted/30',
        sizes.cardPadding,
        className
      )}>
        <div className="flex items-start gap-2">
          {showIcon && (
            <span className={cn('flex-shrink-0 mt-0.5', sizes.icon)}>{icon}</span>
          )}
          <div className="min-w-0">
            <div className={sizes.quadrant}>{quadrantInfo.quadrantName}</div>
            {showSubLabel && (
              <div className={cn('text-muted-foreground mt-0.5', sizes.subLabel)}>
                {quadrantInfo.subLabel}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Default: badge variant
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn(
          'inline-flex items-center gap-1.5 rounded-full',
          'bg-muted/50 border border-border/50',
          sizes.padding,
          className
        )}>
          {showIcon && <span className={sizes.icon}>{icon}</span>}
          <span className={sizes.quadrant}>{quadrantInfo.quadrantName}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <div className="space-y-1">
          <div className="font-medium">{quadrantInfo.quadrantName}</div>
          <div className="text-muted-foreground">{quadrantInfo.subLabel}</div>
          <div className="flex gap-2 text-xs pt-1">
            <span>H: {quadrantInfo.coordinates.x}</span>
            <span>V: {quadrantInfo.coordinates.y}</span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

// ============================================================================
// Compact Badge Variant
// ============================================================================

interface PoliticalBadgeProps {
  ideology: IdeologyVector
  showIcon?: boolean
  className?: string
}

export function PoliticalBadge({
  ideology,
  showIcon = true,
  className,
}: PoliticalBadgeProps) {
  const quadrantInfo = getQuadrantInfo(ideology)
  const icon = QUADRANT_ICONS[quadrantInfo.quadrantName as QuadrantName] || ''

  return (
    <span className={cn(
      'inline-flex items-center gap-1 text-xs',
      'px-1.5 py-0.5 rounded bg-muted/50',
      className
    )}>
      {showIcon && <span>{icon}</span>}
      <span className="truncate max-w-[120px]">{quadrantInfo.quadrantName}</span>
    </span>
  )
}

// ============================================================================
// Full Political Position Display
// ============================================================================

interface PoliticalPositionProps {
  ideology: IdeologyVector
  title?: string
  showCoordinates?: boolean
  className?: string
}

export function PoliticalPosition({
  ideology,
  title,
  showCoordinates = true,
  className,
}: PoliticalPositionProps) {
  const quadrantInfo = getQuadrantInfo(ideology)
  const icon = QUADRANT_ICONS[quadrantInfo.quadrantName as QuadrantName] || ''

  return (
    <div className={cn('space-y-2', className)}>
      {title && (
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {title}
        </h4>
      )}

      <div className="flex items-start gap-3">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{quadrantInfo.quadrantName}</div>
          <div className="text-sm text-muted-foreground">
            {quadrantInfo.subLabel}
          </div>
        </div>
      </div>

      {showCoordinates && (
        <div className="flex gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="font-medium">Horizontal:</span>
            <span className={cn(
              'tabular-nums',
              quadrantInfo.coordinates.x < 0 ? 'text-red-400' : quadrantInfo.coordinates.x > 0 ? 'text-blue-400' : ''
            )}>
              {quadrantInfo.coordinates.x > 0 ? '+' : ''}{quadrantInfo.coordinates.x}
            </span>
            <span className="text-muted-foreground/70">
              ({quadrantInfo.coordinates.x < -33 ? 'Left' : quadrantInfo.coordinates.x > 33 ? 'Right' : 'Center'})
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">Vertical:</span>
            <span className={cn(
              'tabular-nums',
              quadrantInfo.coordinates.y > 0 ? 'text-orange-400' : quadrantInfo.coordinates.y < 0 ? 'text-green-400' : ''
            )}>
              {quadrantInfo.coordinates.y > 0 ? '+' : ''}{quadrantInfo.coordinates.y}
            </span>
            <span className="text-muted-foreground/70">
              ({quadrantInfo.coordinates.y < -33 ? 'Pluralist' : quadrantInfo.coordinates.y > 33 ? 'Centralized' : 'Institutional'})
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
